import { useMemo, useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { projects } from "@/data/site";

const FILTERS = [
    { key: "all", label: "All projects" },
    { key: "ongoing", label: "Ongoing" },
    { key: "completed", label: "Completed" },
    { key: "upcoming", label: "Upcoming" },
];

const EASE = [0.22, 1, 0.36, 1];

export default function Projects() {
    const [params, setParams] = useSearchParams();
    const filter = params.get("filter") || "all";
    const [active, setActive] = useState(filter);

    useEffect(() => setActive(params.get("filter") || "all"), [params]);

    const shown = useMemo(
        () => (active === "all" ? projects : projects.filter((p) => p.status === active)),
        [active]
    );

    const setFilter = (f) => {
        setActive(f);
        if (f === "all") setParams({});
        else setParams({ filter: f });
    };

    return (
        <div data-testid="page-projects">
            <PageHeader
                eyebrow="Selected projects · 2007 → 2029"
                title={<>Thirty-seven, and <em className="italic text-lime">counting slowly.</em></>}
                lead="Ongoing, completed and upcoming. Filter below to see what we are building right now, and what will open next."
            />

            <section className="relative bg-forest-950 py-16 md:py-24 border-b border-white/10">
                <div className="mx-auto max-w-[1440px] px-6 md:px-10">
                    <div className="flex flex-wrap items-center gap-2 md:gap-4">
                        {FILTERS.map((f) => (
                            <button
                                key={f.key}
                                data-testid={`filter-${f.key}`}
                                onClick={() => setFilter(f.key)}
                                className={`px-5 md:px-6 py-3 font-mono-mini text-[11px] tracking-[0.28em] uppercase border transition-all ${
                                    active === f.key
                                        ? "bg-lime border-lime text-forest-900"
                                        : "border-ivory/20 text-ivory/80 hover:border-lime hover:text-lime"
                                }`}
                            >
                                {f.label}
                                <span className="ml-3 text-[10px] opacity-70">
                                    {f.key === "all" ? projects.length : projects.filter((p) => p.status === f.key).length}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            <section className="relative bg-forest-950 py-16 md:py-24">
                <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-x-8 gap-y-16 md:gap-y-24">
                    {shown.map((p, i) => (
                        <motion.article
                            key={p.slug}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-8% 0px" }}
                            transition={{ duration: 0.95, ease: EASE, delay: (i % 3) * 0.06 }}
                            data-testid={`projects-list-item-${p.slug}`}
                            className={`group ${i % 3 === 0 ? "md:col-span-7" : i % 3 === 1 ? "md:col-span-5" : "md:col-span-12"}`}
                        >
                            <Link to={`/projects/${p.slug}`}>
                                <div className={`relative overflow-hidden bg-forest-800 ${i % 3 === 2 ? "aspect-[16/6]" : "aspect-[4/3]"}`}>
                                    <motion.img
                                        src={p.cover}
                                        alt={p.name}
                                        loading="lazy"
                                        className="h-full w-full object-cover"
                                        initial={{ scale: 1.06 }}
                                        whileInView={{ scale: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 1.5, ease: EASE }}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-b from-transparent to-forest-900/70" />
                                    <div className="absolute top-5 left-5 flex items-center gap-3">
                                        <span className="h-2 w-2 bg-lime" />
                                        <span className="font-mono-mini text-[10px] tracking-[0.3em] uppercase text-ivory/90">
                                            {p.status}
                                        </span>
                                    </div>
                                    <div className="absolute bottom-5 right-5 h-11 w-11 flex items-center justify-center bg-lime text-forest-900 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500">
                                        <ArrowUpRight size={18} strokeWidth={1.5} />
                                    </div>
                                </div>

                                <div className="mt-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                                    <div>
                                        <h3 className="font-serif-display text-3xl md:text-[2.2rem] leading-tight text-ivory group-hover:text-lime transition-colors">
                                            {p.name}
                                        </h3>
                                        <div className="mt-2 text-ivory/60 text-sm">
                                            {p.location} — {p.type}
                                        </div>
                                    </div>
                                    <div className="text-left md:text-right">
                                        <div className="font-mono-mini text-[10px] tracking-[0.32em] uppercase text-ivory/50">
                                            {p.units}
                                        </div>
                                        <div className="font-mono-mini text-[10px] tracking-[0.32em] uppercase text-lime mt-1">
                                            {p.year}
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        </motion.article>
                    ))}
                </div>
            </section>
        </div>
    );
}
